import { useState, useEffect, useRef } from "react";
import { useDocPrice, priceLabel } from "@/utils/docPrices";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Sparkles, FileText, Download, CheckCircle, Shield, Clock, Zap, Star,
  Target, Brain, Upload, Layers, ChevronDown, ChevronUp, Briefcase,
  FileCheck, PenTool, Lock, RefreshCw
} from "lucide-react";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView();

  useEffect(() => {
    if (!isInView) return;

    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-slate-200 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full py-5 px-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
    >
      <span className="font-semibold text-slate-900 pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-emerald-700 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-6 pb-5 text-slate-600 leading-relaxed">
        {answer}
      </div>
    )}
  </div>
);

// Template Preview Card
const TemplateCard = ({ name, description, color, popular }) => (
  <div className="group relative bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
    {popular && (
      <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
        POPULAR
      </div>
    )}
    <div className="p-6">
      {/* Mock resume preview */}
      <div className="aspect-[8.5/11] bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden mb-4">
        <div className={`h-12 ${color}`}></div>
        <div className="p-3 space-y-2">
          <div className="h-3 bg-slate-800 rounded w-3/4"></div>
          <div className="h-2 bg-slate-300 rounded w-1/2"></div>
          <div className="mt-3 space-y-1.5">
            <div className="h-2 bg-slate-200 rounded"></div>
            <div className="h-2 bg-slate-200 rounded w-5/6"></div>
            <div className="h-2 bg-slate-200 rounded w-4/6"></div>
          </div>
          <div className="mt-3 space-y-1.5">
            <div className="h-2 bg-slate-100 rounded"></div>
            <div className="h-2 bg-slate-100 rounded w-5/6"></div>
          </div>
        </div>
      </div>
      <h3 className="font-display font-bold text-slate-900">{name}</h3>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
    </div>
    <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <span className="bg-white text-slate-900 font-semibold px-6 py-3 rounded-full shadow-sm">
        Use This Template
      </span>
    </div>
  </div>
);

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

const TONE_CYCLE = ["gray", "mint", "black"];

export default function AIResumeLanding() {
  const navigate = useNavigate();
  // the resume's price as the admin set it (Preferences > Document prices)
  const resumePrice = useDocPrice("ai-resume", 9.99);
  const P = priceLabel(resumePrice);
  const PT = resumePrice === 0 ? "Free" : P;
  const [openFAQ, setOpenFAQ] = useState(0);
  const [heroRef, heroInView] = useInView();
  const [howItWorksRef, howItWorksInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [demoRef, demoInView] = useInView();
  const [templatesRef, templatesInView] = useInView();
  const [testiRef, testiInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const faqs = [
    {
      question: "How does the AI Resume Builder work?",
      answer: "Our AI Resume Builder uses advanced artificial intelligence (powered by GPT-4) to analyze your work experience, skills, and target job description. It then generates optimized bullet points, professional summaries, and suggests relevant keywords to help your resume pass ATS screening systems and impress hiring managers."
    },
    {
      question: "How much does the AI Resume Builder cost?",
      answer: `The AI Resume Builder ${resumePrice === 0 ? "is free" : `costs just ${P} for a one-time payment`}. This includes AI-powered content generation, unlimited regenerations, multiple template options, custom fonts, and instant download in both PDF and DOCX formats. No subscription required!`
    },
    {
      question: "What makes your AI different from other resume builders?",
      answer: "Our AI is specifically trained on thousands of successful resumes across industries. It doesn't just fill in templates. It tailors content to your target job, optimizes for ATS compatibility, provides an ATS score, and suggests improvements. Plus, it can analyze job descriptions to highlight matching keywords."
    },
    {
      question: "Can I upload my existing resume?",
      answer: "Yes! You can upload your existing resume in PDF or DOCX format. Our AI will parse it, extract your information, and auto-fill the form fields. You can then enhance and optimize your content with AI-powered suggestions."
    },
    {
      question: "What file formats can I download?",
      answer: "After payment, you can download your resume as a professional PDF and editable DOCX file. Both formats are optimized for ATS systems and print-ready."
    },
    {
      question: "Are the resumes ATS-friendly?",
      answer: "Absolutely! All our templates are designed with ATS (Applicant Tracking System) compatibility in mind. We use clean formatting, standard fonts, and proper heading structures that ATS software can easily parse. Our AI also includes relevant keywords to improve your chances."
    },
    {
      question: "Can I customize fonts and layouts?",
      answer: "Yes! You can choose from multiple professional fonts (Montserrat, Times New Roman, Calibri, Arial, Helvetica), three template styles (ATS Optimized, Modern, Classic), and layout options. You can also enable a one-page mode for concise resumes."
    },
    {
      question: "How long does it take to create a resume?",
      answer: "Most users complete their AI-optimized resume in under 10 minutes. Simply enter your information, let the AI enhance your content, preview, and download. It's that fast!"
    }
  ];

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Content",
      description: "GPT-4 analyzes your experience and generates compelling, job-specific bullet points and summaries."
    },
    {
      icon: Target,
      title: "ATS Optimization",
      description: "Get an ATS compatibility score and keyword suggestions to pass automated screening systems."
    },
    {
      icon: Upload,
      title: "Resume Upload",
      description: "Upload your existing resume and let our AI extract and enhance your information automatically."
    },
    {
      icon: Layers,
      title: "Multiple Templates",
      description: "Choose from professional, ATS-friendly templates designed by career experts."
    },
    {
      icon: PenTool,
      title: "Custom Fonts",
      description: "Select from 5 professional fonts with embedded PDF support for consistent rendering."
    },
    {
      icon: RefreshCw,
      title: "Unlimited Regeneration",
      description: "Not satisfied? Regenerate any section unlimited times until it's perfect."
    },
    {
      icon: FileCheck,
      title: "Instant Preview",
      description: "See real-time previews of your resume as you build it. No surprises."
    },
    {
      icon: Lock,
      title: "Secure & Private",
      description: "Your data is encrypted and never shared. We respect your privacy."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Enter Your Information",
      description: "Fill in your personal details, work experience, education, and skills. Upload an existing resume to auto-fill.",
      icon: FileText
    },
    {
      number: "02",
      title: "Add Your Target Job",
      description: "Paste the job description you're applying for. Our AI analyzes it to tailor your resume content.",
      icon: Briefcase
    },
    {
      number: "03",
      title: "Let AI Optimize",
      description: "Click generate and watch as AI creates compelling bullet points, summaries, and keyword-optimized content.",
      icon: Sparkles
    },
    {
      number: "04",
      title: "Download & Apply",
      description: `Preview, customize, ${resumePrice === 0 ? "" : `pay ${P}, `}and download your professional resume in PDF and DOCX formats.`,
      icon: Download
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      role: "Marketing Manager",
      company: "Hired at Fortune 500",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
      quote: "The AI suggestions were incredibly relevant to my industry. I got 3 interview calls within a week of using my new resume!",
      rating: 5
    },
    {
      name: "James K.",
      role: "Software Engineer",
      company: "Landed Dream Job",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      quote: "Finally, a resume builder that understands tech roles. The ATS optimization feature is a game-changer.",
      rating: 5
    },
    {
      name: "Emily R.",
      role: "Recent Graduate",
      company: "First Job Success",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      quote: "As a new grad with limited experience, the AI helped me highlight transferable skills I didn't know I had.",
      rating: 5
    }
  ];

  const templates = [
    { name: "ATS Optimized", description: "Clean, scannable format", color: "bg-slate-800", popular: true },
    { name: "Modern Professional", description: "Contemporary with accent colors", color: "bg-emerald-700", popular: false },
    { name: "Classic Executive", description: "Traditional formal style", color: "bg-slate-700", popular: false }
  ];

  const stats = [
    { value: 50000, suffix: "+", label: "Resumes Created", tone: "mint" },
    { value: 92, suffix: "%", label: "Avg. ATS Score", tone: "black" },
    { value: 10000, suffix: "+", label: "Job Offers", tone: "gray" },
    { value: 5, suffix: " min", label: "Average Time", tone: "black" }
  ];

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>{`AI Resume Builder | Create ATS-Optimized Resumes for ${P} | MintSlip`}</title>
        <meta name="description" content={`Build a professional, ATS-optimized resume in minutes with our AI Resume Builder. GPT-4 powered content generation, multiple templates, instant download. ${resumePrice === 0 ? "Free!" : `Only ${P}!`}`} />
        <meta name="keywords" content="AI resume builder, resume builder, ATS resume, resume generator, resume maker, professional resume, job application, career, CV builder, resume templates 2025" />
        <meta property="og:title" content={`AI Resume Builder | Create ATS-Optimized Resumes for ${P}`} />
        <meta property="og:description" content="Build a professional, ATS-optimized resume in minutes with AI. GPT-4 powered content, multiple templates, instant PDF download." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`AI Resume Builder for ${P} | MintSlip`} />
        <meta name="twitter:description" content="Create ATS-optimized resumes with AI. Land more interviews with professionally crafted content." />
        <link rel="canonical" href="https://mintslip.com/resume-builder" />

        {/* Schema.org structured data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "MintSlip AI Resume Builder",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "9.99",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "2847"
            },
            "description": "AI-powered resume builder that creates ATS-optimized professional resumes in minutes."
          }
        `}</script>
      </Helmet>

      <Header />

      {/* Hero — centered like the home hero, with the mockup + stats bento below */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-4">
            <Sparkles className="w-4 h-4" />
            AI-Powered Resume Builder
          </p>
          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Create an <span className="font-black">ATS-optimized resume</span> in minutes
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-6">
            Let AI craft compelling content tailored to your target job. Our AI-powered builder helps you
            <span className="font-semibold text-slate-900"> land 3x more interviews</span> with professionally optimized resumes.
          </p>

          {/* Price line */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-slate-100 rounded-full">
              <span className="text-slate-600 text-sm">One-time payment</span>
              <span className="font-display text-2xl font-black text-slate-900">{PT}</span>
              <span className="text-slate-600 text-sm">PDF + DOCX</span>
            </div>
          </div>

          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/resumes")}>
              <FileText size={18} />
              <span>Build my resume</span>
            </button>
          </div>
          <div className="flex justify-center mb-8">
            <button
              type="button"
              onClick={() => navigate("/app/resumes")}
              className="inline-flex items-center gap-2 text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload existing resume
            </button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-14">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-sm text-slate-500">4.8/5 from 2,847 reviews</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Shield className="w-4 h-4 text-emerald-700" />
              <span>Secure payment</span>
            </div>
          </div>

          {/* Mockup + stats bento */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 items-stretch">
            {/* Resume preview mockup */}
            <div className="rounded-[28px] bg-slate-100 p-8 md:p-10 flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-slate-800 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">JD</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">John Doe</h3>
                        <p className="text-emerald-400">Senior Software Engineer</p>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    {/* AI indicator */}
                    <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full w-fit">
                      <Sparkles className="w-4 h-4" />
                      <span className="font-medium">AI-Enhanced Content</span>
                    </div>

                    <div className="space-y-3">
                      <div className="h-3 bg-slate-200 rounded w-full animate-pulse"></div>
                      <div className="h-3 bg-slate-200 rounded w-5/6 animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                      <div className="h-3 bg-slate-200 rounded w-4/6 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    </div>

                    <div className="pt-2 space-y-2">
                      <div className="h-2 bg-slate-100 rounded"></div>
                      <div className="h-2 bg-slate-100 rounded w-5/6"></div>
                      <div className="h-2 bg-slate-100 rounded w-4/5"></div>
                    </div>

                    {/* ATS Score */}
                    <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg mt-4">
                      <span className="font-semibold text-slate-700">ATS Score</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="w-[92%] h-full bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-full"></div>
                        </div>
                        <span className="font-bold text-emerald-700">92%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2 animate-bounce" style={{ animationDuration: '3s' }}>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-semibold text-slate-700">ATS Approved</span>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-semibold text-slate-700">Ready in 5 min</span>
                </div>
              </div>
            </div>

            {/* Stats bento */}
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {stats.map((stat) => {
                const t = toneCard[stat.tone];
                return (
                  <div key={stat.label} className={`rounded-[28px] ${t.bg} px-4 py-10 md:px-6 md:py-12 flex flex-col items-center justify-center text-center`}>
                    <span className={`font-display font-black text-3xl sm:text-4xl md:text-5xl ${t.big}`}>
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </span>
                    <span className={`mt-3 text-sm md:text-base ${t.small}`}>{stat.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section ref={howItWorksRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-24">
        <p className={`text-center text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-3 transition-all duration-700 ${howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          How it works
        </p>
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Build your resume in <span className="font-black">4 easy steps</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Our AI-powered process makes resume creation effortless. From data entry to download,
          you'll have a professional resume in minutes.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, index) => {
            const t = toneCard[TONE_CYCLE[index % TONE_CYCLE.length]];
            return (
              <div
                key={step.number}
                className={`rounded-[28px] ${t.bg} p-8 transition-all duration-700 ${howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + index * 120}ms` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
                    <step.icon className="w-7 h-7 text-slate-900" />
                  </span>
                  <span className={`font-display text-4xl font-black opacity-30 ${t.big}`}>{step.number}</span>
                </div>
                <h3 className={`font-display text-2xl font-medium mb-2 ${t.big}`}>{step.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{step.description}</p>
              </div>
            );
          })}
        </div>

        <div className={`flex justify-center mt-12 transition-all duration-700 delay-300 ${howItWorksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <button className="navbar-cta" onClick={() => navigate("/app/resumes")}>
            <FileText size={18} />
            <span>Start building now ({PT})</span>
          </button>
        </div>
      </section>

      {/* Features Grid */}
      <section ref={featuresRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <p className={`text-center text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-3 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Powerful features
        </p>
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Everything you need to <span className="font-black">land your dream job</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Our AI Resume Builder comes packed with features designed to give you a competitive edge.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feature, index) => {
            const t = toneCard[TONE_CYCLE[index % TONE_CYCLE.length]];
            return (
              <div
                key={feature.title}
                className={`rounded-[28px] ${t.bg} p-7 md:p-8 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + index * 100}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <feature.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-xl font-medium mb-2 ${t.big}`}>{feature.title}</h3>
                <p className={`text-sm leading-relaxed ${t.small}`}>{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* AI Demo Section */}
      <section ref={demoRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center transition-all duration-700 ${demoInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div>
            <p className="text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-3">
              AI-powered
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-6">
              Let AI write your <span className="font-black">perfect bullet points</span>
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Simply describe your role and responsibilities, and our AI will generate compelling,
              achievement-focused bullet points optimized for ATS systems and hiring managers.
            </p>

            <div className="space-y-4">
              {[
                "Tailored to your target job description",
                "Uses powerful action verbs and metrics",
                "Optimized with industry-specific keywords",
                "Regenerate until you're satisfied"
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </span>
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <button className="navbar-cta" onClick={() => navigate("/app/resumes")}>
                <Sparkles size={18} />
                <span>Try AI generation ({PT})</span>
              </button>
            </div>
          </div>

          <div className="rounded-[28px] bg-slate-100 p-6 md:p-10">
            {/* Input */}
            <div className="mb-6">
              <label className="text-sm text-slate-500 mb-2 block">Your Input:</label>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-slate-700">"managed team of developers, worked on web applications"</p>
              </div>
            </div>

            {/* AI Output */}
            <div>
              <label className="text-sm text-emerald-700 font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI-Generated:
              </label>
              <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
                {[
                  "Led cross-functional team of 8 developers, delivering 15+ web applications with 99.9% uptime",
                  "Spearheaded agile transformation, reducing sprint cycle time by 40% and increasing team velocity",
                  "Architected scalable microservices infrastructure serving 100K+ daily active users"
                ].map((bullet) => (
                  <div key={bullet} className="flex items-start gap-3">
                    <span className="text-emerald-600 mt-1">•</span>
                    <p className="text-slate-800">{bullet}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section ref={templatesRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <p className={`text-center text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-3 transition-all duration-700 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Professional templates
        </p>
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Choose your <span className="font-black">perfect template</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          All templates are ATS-friendly and designed by career experts to help you stand out.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {templates.map((template, index) => (
            <div
              key={template.name}
              className={`cursor-pointer transition-all duration-700 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + index * 120}ms` }}
              onClick={() => navigate("/app/resumes")}
            >
              <TemplateCard {...template} />
            </div>
          ))}
        </div>

        <div className={`flex justify-center mt-10 transition-all duration-700 delay-300 ${templatesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <button
            type="button"
            onClick={() => navigate("/app/resumes")}
            className="text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
          >
            View all templates
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testiRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <p className={`text-center text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-3 transition-all duration-700 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Success stories
        </p>
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Join thousands of <span className="font-black">happy job seekers</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          See how our AI Resume Builder has helped professionals land their dream jobs.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.name}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-7 transition-all duration-700 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + index * 120}ms` }}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">"{testimonial.quote}"</p>
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                  <p className="text-sm text-emerald-700 font-medium">{testimonial.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <p className={`text-center text-sm font-semibold tracking-widest uppercase text-emerald-700 mb-3 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          FAQ
        </p>
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Frequently asked <span className="font-black">questions</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Everything you need to know about our AI Resume Builder.
        </p>

        <div className={`max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all duration-700 delay-150 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFAQ === index}
              onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
            />
          ))}
        </div>
      </section>

      {/* Final CTA Section */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to build your <span className="font-black">winning resume?</span>
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-6">
            Join over 50,000 job seekers who've landed interviews with our AI-powered resume builder.
          </p>

          {/* Price highlight */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-slate-100 rounded-full">
              <span className="text-slate-600">Only</span>
              <span className="font-display text-3xl font-black text-slate-900">{PT}</span>
              <span className="text-slate-600">one-time payment</span>
            </div>
          </div>

          <div className="flex justify-center mb-8">
            <button className="navbar-cta" onClick={() => navigate("/app/resumes")}>
              <FileText size={18} />
              <span>Build my resume</span>
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-700" />
              <span>PDF + DOCX download</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-700" />
              <span>Ready in 5 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-700" />
              <span>Secure payment</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
